import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type Draft } from "../lib/api";

interface ApproveArgs {
  id: string;
  scheduledAt?: string;
}

export const useApproveMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scheduledAt }: ApproveArgs) => api.drafts.approve(id, scheduledAt),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: ["drafts"] });
      const previous = qc.getQueryData<Draft[]>(["drafts"]);
      qc.setQueryData<Draft[]>(["drafts"], (old) => (old ?? []).filter((d) => d.id !== id));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(["drafts"], ctx.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["drafts"] });
      qc.invalidateQueries({ queryKey: ["scheduled"] });
    },
  });
};

export const useRejectMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.drafts.reject(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["drafts"] });
      const previous = qc.getQueryData<Draft[]>(["drafts"]);
      qc.setQueryData<Draft[]>(["drafts"], (old) => (old ?? []).filter((d) => d.id !== id));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(["drafts"], ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["drafts"] }),
  });
};

export const useUpdateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      api.drafts.update(id, content),
    onSuccess: (updated) => {
      qc.setQueryData(["draft", updated.id], updated);
    },
  });
};
